import { z } from "zod";

import { classementAction, connecteurActif, strategieDeclencheur } from "./connecteurs.js";
import { FluxEtapeAgentChampsSchema, type FluxEtapeAgentChamps } from "./flux-agents.js";
import type { Connexion } from "./connexions.js";
import { resoudre } from "./flux-expressions.js";

/**
 * FLUX : une AUTOMATISATION, ecrite en DECLARATIF.
 *
 * Un flux est un document JSON : un DECLENCHEUR, puis une liste d'ETAPES. Ce
 * schema est le seul contrat : l'app Flux (le builder), le MCP, Lia et le
 * moteur (`functions/src/flux/`) lisent et ecrivent la MEME forme, validee
 * ici. Le serveur le recoit par le bundle `functions/build-studio.mjs`
 * (`functions/` n'importe aucun `@taki/*`). Le modele reprend celui
 * d'Activepieces (declencheur, actions, routeur a branches, boucle,
 * approbation, delai), en noms francais et en arbre plutot qu'en liste
 * chainee : un humain ou une IA relit un tableau d'etapes, pas une chaine de
 * `nextAction`.
 *
 * DEUX COPIES DE LA DEFINITION. `brouillon` est ce que le builder edite ;
 * `publie` est ce que le moteur execute. « Publier » recopie l'un dans
 * l'autre : on peut retoucher une automatisation active sans la casser.
 *
 * LES CONNECTEURS. Une etape ou un declencheur `connecteur` vise une piece
 * d'Activepieces ACTIVE (`connecteurs.ts`), executee par le pont
 * (`docs/socle/flux.md`, « Connecteurs Activepieces »).
 *
 * LES REFERENCES. Une valeur d'entree peut citer le contexte entre doubles
 * accolades, comme dans Activepieces : `{{trigger.fiche.name}}`,
 * `{{step_1.totalMinutes}}`, `{{vars.cle}}`, `{{date.hier}}`. Une valeur qui
 * n'est QUE `{{...}}` garde son type (nombre, liste, objet) ; sinon le texte
 * est interpole. Le moteur des references vit dans `flux-expressions.ts`.
 */

export const FLUX_SCHEMA_VERSION = 1 as const;

/** Les deux PROJETS du rail : partage a l'org, ou personnel a son auteur. */
export const FLUX_PORTEES = ["org", "perso"] as const;
export type FluxPortee = (typeof FLUX_PORTEES)[number];

export const FLUX_TYPES_DECLENCHEUR = ["manuel", "planification", "fiche", "webhook", "connecteur", "bouton"] as const;
export type FluxTypeDeclencheur = (typeof FLUX_TYPES_DECLENCHEUR)[number];

/**
 * Les evenements d'une fiche qui declenchent.
 * - `cree`, `modifie` (tout changement, ou celui d'un `champ`), `archive`, `restaure` (sortie des archives) ;
 * - `champ` : UN champ change, de la valeur `de` (facultative) a la valeur `vers` (facultative) ;
 * - `date` : la date d'un champ est atteinte, avec un `decalage` facultatif (3 jours avant l'echeance) ;
 * - `lien` : une fiche est liee par un champ lien (a la creation comme a la modification) ;
 * - `fichier` : une piece jointe est deposee sur la fiche.
 */
export const FLUX_EVENEMENTS_FICHE = ["cree", "modifie", "champ", "archive", "restaure", "date", "lien", "fichier"] as const;
export type FluxEvenementFiche = (typeof FLUX_EVENEMENTS_FICHE)[number];
/** Les evenements qui exigent un `champ`. */
export const FLUX_EVENEMENTS_A_CHAMP: readonly FluxEvenementFiche[] = ["champ", "date", "lien"];
export const FLUX_UNITES_DECALAGE = ["minutes", "heures", "jours"] as const;
/** L'heure d'une date sans heure (`YYYY-MM-DD`) quand le declencheur `date` n'en dit pas. */
export const FLUX_HEURE_DATE_PAR_DEFAUT = "09:00";
/** Conditions d'un declencheur fiche, au plus. */
export const FLUX_CONDITIONS_FICHE_MAX = 10;

export const FLUX_TYPES_ETAPE = ["taki", "http", "donnees", "branches", "boucle", "delai", "approbation", "connecteur", "agent"] as const;
export type FluxTypeEtape = (typeof FLUX_TYPES_ETAPE)[number];

export const FLUX_METHODES_HTTP = ["GET", "POST", "PUT", "PATCH", "DELETE"] as const;
export const FLUX_UNITES_DELAI = ["secondes", "minutes", "heures", "jours"] as const;
export const FLUX_CANAUX_APPROBATION = ["interne", "email", "whatsapp"] as const;

/**
 * Les operateurs de condition du routeur, ceux d'Activepieces
 * (`BranchOperator`), en francais. Les operateurs unaires n'ont pas de
 * `droite`.
 */
export const FLUX_OPERATEURS = [
  "texte_contient",
  "texte_ne_contient_pas",
  "texte_egal",
  "texte_different",
  "texte_commence_par",
  "texte_finit_par",
  "nombre_superieur",
  "nombre_inferieur",
  "nombre_egal",
  "booleen_vrai",
  "booleen_faux",
  "existe",
  "n_existe_pas",
  "liste_contient",
  "liste_vide",
  "liste_non_vide",
  "date_avant",
  "date_apres",
] as const;
export type FluxOperateur = (typeof FLUX_OPERATEURS)[number];
export const FLUX_OPERATEURS_UNAIRES: readonly FluxOperateur[] = ["booleen_vrai", "booleen_faux", "existe", "n_existe_pas", "liste_vide", "liste_non_vide"];

/** Le fuseau d'une org sans reglage (`FUSEAU_PAR_DEFAUT`, `jour.ts`). */
export const FLUX_FUSEAU_PAR_DEFAUT = "Europe/Luxembourg";

/** Plafonds : un flux se relit, il ne s'empile pas. */
export const FLUX_ETAPES_MAX = 60;
export const FLUX_PROFONDEUR_MAX = 4;
export const FLUX_BRANCHES_MAX = 10;

/**
 * LES OPERATIONS DE LA CALLABLE `fluxApi`, la liste UNIQUE. Un client ne peut
 * appeler qu'une operation de cette liste (`sdk.flux.appeler` est type
 * `OperationFlux`) : une faute de frappe, ou une operation que personne n'a
 * declaree, ne compile pas. `declencherBouton` est le clic d'un smart bouton
 * (`docs/socle/smart-boutons.md`). Le serveur (`functions/src/flux/api.ts`) ne peut
 * pas importer ce paquet ; un test (`functions/tests/flux-operations.test.mjs`)
 * exige un `case` par operation, et aucun `case` hors liste.
 */
export const OPERATIONS_FLUX = [
  "catalogue",
  "connecteurs",
  "connecteur_options",
  "taki_options",
  "creer",
  "modifier",
  "publier",
  "activer",
  "dupliquer",
  "archiver",
  "restaurer",
  "supprimer",
  "tester",
  "lancer",
  "approuver",
  "arreter",
  "variable_creer",
  "variable_modifier",
  "variable_supprimer",
  "webhook",
  "horloge",
  "cles",
  "cle_creer",
  "cle_modifier",
  "cle_supprimer",
  "declencherBouton",
] as const;
export type OperationFlux = (typeof OPERATIONS_FLUX)[number];

const ID_ETAPE = /^[a-z][a-z0-9_]{0,39}$/;
const FAMILLE = /^[A-Za-z][A-Za-z0-9]{0,63}$/;
const NOM_ACTION = /^[a-z][a-z0-9_]{0,79}$/;
const NOM_VARIABLE = /^[A-Za-z_][A-Za-z0-9_]{0,63}$/;
/** Un nom d'action ou de declencheur d'une piece Activepieces : `insert_row`, `new-worksheet`, `getRowById`. */
const NOM_PIECE = /^[A-Za-z][A-Za-z0-9_-]{0,119}$/;
/** Un connecteur ACTIF (`connecteurs.ts`) : les autres sont « Bientôt », jamais dans une definition. */
const CONNECTEUR = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .refine((id) => connecteurActif(id) !== undefined, "Connecteur inconnu ou pas encore actif.");

const nom = z.string().trim().min(1, "Nom requis.").max(80, "Nom trop long (80 caracteres).");

// ---------------------------------------------------------------------------
// Declencheurs
// ---------------------------------------------------------------------------

export const FluxDeclencheurManuelSchema = z.object({ type: z.literal("manuel") }).strict();

export const FluxDeclencheurPlanificationSchema = z
  .object({
    type: z.literal("planification"),
    /** Cron a cinq champs (minute heure jour mois jour-de-semaine), lu dans `fuseau`. */
    cron: z.string().trim().min(9).max(120),
    /** Un fuseau explicite ; absent, celui de l'organisation au moment du passage. */
    fuseau: z.string().trim().min(1).max(64).optional(),
  })
  .strict();

/**
 * UNE CONDITION SUR LA FICHE du declencheur : un champ (chemin `customFields.x`
 * admis), un operateur du routeur, une valeur en TEXTE (la valeur stockee : la
 * cle d'un choix, le `personId` d'une personne, `true`, un nombre). Elle se lit
 * sur la fiche APRES l'evenement, comme `{{trigger.fiche.<champ>}}`.
 */
export const FluxConditionFicheSchema = z
  .object({
    champ: z.string().trim().min(1).max(120),
    operateur: z.enum(FLUX_OPERATEURS),
    valeur: z.string().max(2000).optional(),
  })
  .strict();
export type FluxConditionFiche = z.infer<typeof FluxConditionFicheSchema>;

export const FluxDeclencheurFicheSchema = z
  .object({
    type: z.literal("fiche"),
    evenement: z.enum(FLUX_EVENEMENTS_FICHE),
    famille: z.string().regex(FAMILLE, "Famille invalide."),
    /** Sous-type de la fiche (`fields.type`), facultatif. */
    typeFiche: z.string().trim().min(1).max(64).optional(),
    /**
     * Le champ surveille (chemin `customFields.x` admis). `modifie` : facultatif,
     * seulement si ce champ change ; `champ`, `date`, `lien` : requis.
     */
    champ: z.string().trim().min(1).max(120).optional(),
    /** `champ` : la valeur d'AVANT (stockee, en texte) ; absente, n'importe laquelle. */
    de: z.string().trim().min(1).max(500).optional(),
    /** `champ` : la valeur d'APRES (stockee, en texte) ; absente, n'importe laquelle. */
    vers: z.string().trim().min(1).max(500).optional(),
    /** `date` : partir avant ou apres la date du champ ; absent, a la date meme. */
    decalage: z
      .object({ valeur: z.number().int().min(0).max(10000), unite: z.enum(FLUX_UNITES_DECALAGE), sens: z.enum(["avant", "apres"]) })
      .strict()
      .optional(),
    /** `date` : l'heure d'une date sans heure, `HH:MM` dans le fuseau de l'org ; absente, 09:00. */
    heure: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Heure invalide (HH:MM).").optional(),
    /** Conditions sur la fiche, toutes vraies (`conditionsMode: 'toutes'`, defaut) ou une au moins (`'une'`). */
    conditions: z.array(FluxConditionFicheSchema).max(FLUX_CONDITIONS_FICHE_MAX).optional(),
    conditionsMode: z.enum(["toutes", "une"]).optional(),
  })
  .strict();

export const FluxDeclencheurWebhookSchema = z.object({ type: z.literal("webhook") }).strict();

/**
 * Un declencheur d'un CONNECTEUR (Activepieces, `connecteurs.ts`) : sondage
 * chaque minute ou webhook enregistre a l'activation, selon sa strategie.
 * `{{trigger}}` vaut l'evenement recu. `connexion` : l'id d'une connexion du
 * socle, du fournisseur du connecteur. Que ce declencheur soit permis se
 * verifie dans `verifierFlux` (Zod ne croise pas deux champs d'une union).
 */
export const FluxDeclencheurConnecteurSchema = z
  .object({
    type: z.literal("connecteur"),
    connecteur: CONNECTEUR,
    declencheur: z.string().regex(NOM_PIECE, "Declencheur inconnu."),
    entrees: z.record(z.unknown()).default({}),
    connexion: z.string().trim().min(1).max(128).optional(),
  })
  .strict();

/**
 * Un id d'app, de porte ou d'action de porte : minuscules, chiffres, tirets
 * (`appScreenActionSchema`, `docs/socle/smart-boutons.md`). Vide admis : un
 * brouillon se range a moitie rempli, `problemesDePublication` l'exige plein.
 */
const ID_DE_BOUTON = /^(?:[a-z][a-z0-9-]{0,63})?$/;

/**
 * Le declencheur « Bouton » : le clic d'un smart bouton de l'app `app`, sur la
 * porte `porte`, action `bouton` (une action de porte `flux: true`). La
 * declaration de l'app ne nomme aucun flux : c'est le flux qui choisit son
 * bouton. Il vaut pour l'organisation (`refusDeBouton`).
 */
export const FluxDeclencheurBoutonSchema = z
  .object({
    type: z.literal("bouton"),
    app: z.string().regex(ID_DE_BOUTON, "Identifiant d'app invalide."),
    porte: z.string().regex(ID_DE_BOUTON, "Identifiant de porte invalide."),
    bouton: z.string().regex(ID_DE_BOUTON, "Identifiant de bouton invalide."),
  })
  .strict();
export type FluxDeclencheurBouton = z.infer<typeof FluxDeclencheurBoutonSchema>;

/** La CLE d'un bouton : `app/porte/bouton`, celle sur laquelle un flux s'abonne (`declencheurCle` du document flux). */
export function cleDeBouton(app: string, porte: string, bouton: string): string {
  return `${app}/${porte}/${bouton}`;
}

/** La phrase de refus d'un flux a declencheur bouton en portee `perso`, ou `null`. */
export function refusDeBouton(portee: FluxPortee, declencheur: Pick<FluxDeclencheur, "type">): string | null {
  return declencheur.type === "bouton" && portee === "perso" ? "Un flux lancé par un bouton est un flux d'organisation." : null;
}

export const FluxDeclencheurSchema = z.discriminatedUnion("type", [
  FluxDeclencheurManuelSchema,
  FluxDeclencheurPlanificationSchema,
  FluxDeclencheurFicheSchema,
  FluxDeclencheurWebhookSchema,
  FluxDeclencheurConnecteurSchema,
  FluxDeclencheurBoutonSchema,
]);
export type FluxDeclencheur = z.infer<typeof FluxDeclencheurSchema>;
export type FluxDeclencheurFiche = z.infer<typeof FluxDeclencheurFicheSchema>;

// ---------------------------------------------------------------------------
// Etapes
// ---------------------------------------------------------------------------

export const FluxConditionSchema = z
  .object({
    gauche: z.string().max(2000),
    operateur: z.enum(FLUX_OPERATEURS),
    droite: z.string().max(2000).optional(),
  })
  .strict();
export type FluxCondition = z.infer<typeof FluxConditionSchema>;

interface EtapeCommune {
  /** Identifiant cite par les references : `step_1`, `step_2`... */
  id: string;
  nom: string;
  /** Une etape desactivee est sautee (sortie vide). */
  desactivee?: boolean;
  /** En cas d'erreur, l'execution continue (l'erreur reste notee). */
  continuerSiErreur?: boolean;
}
export interface FluxEtapeTaki extends EtapeCommune {
  type: "taki";
  /** Nom d'une action du catalogue (`functions/src/actions/`), le meme que le MCP. */
  action: string;
  entrees: Record<string, unknown>;
}
export interface FluxEtapeHttp extends EtapeCommune {
  type: "http";
  methode: (typeof FLUX_METHODES_HTTP)[number];
  url: string;
  entetes?: Record<string, string>;
  parametres?: Record<string, string>;
  corps?: unknown;
  /** Id d'une connexion du socle (`connexions`, service `http`) dont le secret signe la requete. */
  connexion?: string;
  delaiMaxSecondes?: number;
}
export interface FluxEtapeDonnees extends EtapeCommune {
  type: "donnees";
  /** Les valeurs produites, references resolues (le « Data Mapper » d'Activepieces). */
  valeurs: Record<string, unknown>;
}
export interface FluxBranche {
  id: string;
  nom: string;
  /** Groupes en OU, conditions en ET dans un groupe (comme Activepieces). */
  conditions: FluxCondition[][];
  etapes: FluxEtape[];
}
export interface FluxEtapeBranches extends EtapeCommune {
  type: "branches";
  /** `premiere` : la premiere branche vraie ; `toutes` : chaque branche vraie. */
  mode: "premiere" | "toutes";
  branches: FluxBranche[];
  /** Ce qui s'execute si aucune branche n'est vraie. */
  sinon: FluxEtape[];
}
export interface FluxEtapeBoucle extends EtapeCommune {
  type: "boucle";
  /** Reference d'une liste : `{{step_1.records}}`. Dans la boucle : `{{step_N.item}}`, `{{step_N.index}}`. */
  elements: string;
  etapes: FluxEtape[];
}
export interface FluxEtapeDelai extends EtapeCommune {
  type: "delai";
  mode: "pour" | "jusqua";
  duree?: { valeur: number; unite: (typeof FLUX_UNITES_DELAI)[number] };
  /** Instant ISO ou reference, si `mode` vaut `jusqua`. */
  jusqua?: string;
}
export interface FluxEtapeApprobation extends EtapeCommune {
  type: "approbation";
  /** Emails des membres qui peuvent decider ; vide : l'auteur. */
  destinataires: string[];
  message: string;
  canaux: (typeof FLUX_CANAUX_APPROBATION)[number][];
  expireApresHeures?: number;
}
/**
 * Une action d'un CONNECTEUR (Activepieces, `connecteurs.ts`), executee par le
 * pont avec la connexion de l'auteur. `entrees` : les proprietes de l'action
 * (references `{{...}}` admises), `connexion` : l'id d'une connexion du socle,
 * du fournisseur du connecteur.
 */
export interface FluxEtapeConnecteur extends EtapeCommune {
  type: "connecteur";
  connecteur: string;
  action: string;
  entrees: Record<string, unknown>;
  connexion?: string;
}
/**
 * Un AGENT : une consigne confiee a un modele, avec des outils choisis, une
 * cle API de l'org et un plafond de depense (`flux-agents.ts`). Sortie :
 * `FluxSortieAgent` (`{{step_N.texte}}`, `{{step_N.donnees.<cle>}}`).
 */
export interface FluxEtapeAgent extends EtapeCommune, FluxEtapeAgentChamps {
  type: "agent";
}
export type FluxEtape =
  | FluxEtapeAgent
  | FluxEtapeConnecteur
  | FluxEtapeTaki
  | FluxEtapeHttp
  | FluxEtapeDonnees
  | FluxEtapeBranches
  | FluxEtapeBoucle
  | FluxEtapeDelai
  | FluxEtapeApprobation;

const commun = {
  id: z.string().regex(ID_ETAPE, "Identifiant d'etape invalide (ex. step_1)."),
  nom,
  desactivee: z.boolean().optional(),
  continuerSiErreur: z.boolean().optional(),
};

export const FluxEtapeSchema: z.ZodType<FluxEtape, z.ZodTypeDef, unknown> = z.lazy(() =>
  z.discriminatedUnion("type", [
    z.object({ ...commun, type: z.literal("taki"), action: z.string().regex(NOM_ACTION, "Action inconnue."), entrees: z.record(z.unknown()).default({}) }).strict(),
    z
      .object({
        ...commun,
        type: z.literal("http"),
        methode: z.enum(FLUX_METHODES_HTTP),
        url: z.string().trim().min(1).max(2000),
        entetes: z.record(z.string().max(4000)).optional(),
        parametres: z.record(z.string().max(2000)).optional(),
        corps: z.unknown().optional(),
        connexion: z.string().trim().min(1).max(128).optional(),
        delaiMaxSecondes: z.number().int().min(1).max(60).optional(),
      })
      .strict(),
    z.object({ ...commun, type: z.literal("donnees"), valeurs: z.record(z.unknown()) }).strict(),
    z.object({ ...commun, type: z.literal("agent"), ...FluxEtapeAgentChampsSchema }).strict(),
    z
      .object({
        ...commun,
        type: z.literal("connecteur"),
        connecteur: CONNECTEUR,
        action: z.string().regex(NOM_PIECE, "Action inconnue."),
        entrees: z.record(z.unknown()).default({}),
        connexion: z.string().trim().min(1).max(128).optional(),
      })
      .strict(),
    z
      .object({
        ...commun,
        type: z.literal("branches"),
        mode: z.enum(["premiere", "toutes"]).default("premiere"),
        branches: z
          .array(
            z
              .object({
                id: z.string().regex(ID_ETAPE),
                nom,
                conditions: z.array(z.array(FluxConditionSchema).min(1).max(10)).min(1).max(10),
                etapes: z.array(FluxEtapeSchema).max(FLUX_ETAPES_MAX),
              })
              .strict(),
          )
          .min(1)
          .max(FLUX_BRANCHES_MAX),
        sinon: z.array(FluxEtapeSchema).max(FLUX_ETAPES_MAX).default([]),
      })
      .strict(),
    z.object({ ...commun, type: z.literal("boucle"), elements: z.string().trim().min(1).max(2000), etapes: z.array(FluxEtapeSchema).max(FLUX_ETAPES_MAX) }).strict(),
    z
      .object({
        ...commun,
        type: z.literal("delai"),
        mode: z.enum(["pour", "jusqua"]),
        duree: z.object({ valeur: z.number().int().min(1).max(100000), unite: z.enum(FLUX_UNITES_DELAI) }).strict().optional(),
        jusqua: z.string().trim().min(1).max(2000).optional(),
      })
      .strict(),
    z
      .object({
        ...commun,
        type: z.literal("approbation"),
        destinataires: z.array(z.string().trim().toLowerCase().email()).max(20).default([]),
        message: z.string().trim().min(1).max(2000),
        canaux: z.array(z.enum(FLUX_CANAUX_APPROBATION)).min(1).max(3).default(["interne"]),
        expireApresHeures: z.number().int().min(1).max(24 * 30).optional(),
      })
      .strict(),
  ]),
) as z.ZodType<FluxEtape, z.ZodTypeDef, unknown>;

export const FluxDefinitionSchema = z
  .object({
    declencheur: FluxDeclencheurSchema,
    etapes: z.array(FluxEtapeSchema).max(FLUX_ETAPES_MAX),
  })
  .strict();
export type FluxDefinition = z.infer<typeof FluxDefinitionSchema>;

/**
 * Ce que l'on DECRIT d'une automatisation (MCP, Lia, builder) : nom,
 * description, projet, definition. Le reste du document est machine.
 */
export const FluxSaisieSchema = z
  .object({
    nom,
    description: z.string().trim().max(500).default(""),
    portee: z.enum(FLUX_PORTEES).default("perso"),
    definition: FluxDefinitionSchema,
  })
  .strict();
export type FluxSaisie = z.infer<typeof FluxSaisieSchema>;

export const FLUX_STATUTS_EXECUTION = ["en_cours", "en_pause", "reussi", "echec", "arrete"] as const;
export type FluxStatutExecution = (typeof FLUX_STATUTS_EXECUTION)[number];
export const FLUX_STATUTS_ETAPE = ["reussi", "echec", "saute", "en_pause", "en_cours"] as const;
export type FluxStatutEtape = (typeof FLUX_STATUTS_ETAPE)[number];

/** Le document `orgs/{orgId}/flux/{fluxId}`, tel que le lit un client. */
export interface Flux {
  id: string;
  orgId: string;
  schemaVersion: 1;
  nom: string;
  description: string;
  portee: FluxPortee;
  actif: boolean;
  brouillon: FluxDefinition;
  publie: FluxDefinition | null;
  version: number;
  publieAt: Date | null;
  auteurUid: string;
  auteurEmail: string;
  /** Type du declencheur publie (ou du brouillon), recopie pour lister et filtrer. */
  declencheurType: FluxTypeDeclencheur;
  /** Declencheur « bouton » seulement : la clef `app/porte/bouton` (`cleDeBouton`), sur laquelle on retrouve les flux d'un bouton. Absente sinon. */
  declencheurCle?: string | null;
  prochainPassage: Date | null;
  /** Le dernier passage ; `test` : un essai du brouillon. */
  dernierRun: { id: string; statut: FluxStatutExecution; at: Date; test?: boolean } | null;
  /** Le chemin du webhook entrant, si le declencheur publie en est un (le jeton reste serveur). */
  webhook: { url: string } | null;
  archivedAt: Date | null;
  /** Supprimee : archivee ET datee, introuvable partout. Absent des documents anterieurs a ce geste. */
  deletedAt?: Date | null;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  updatedBy: string;
}

/** Une etape d'execution, telle que l'ecran Executions la montre. */
export interface FluxExecutionEtape {
  /** Chemin de l'etape : `step_3`, ou `step_2[1].step_4` dans une boucle. */
  chemin: string;
  id: string;
  nom: string;
  type: FluxTypeEtape | "declencheur";
  statut: FluxStatutEtape;
  entree?: unknown;
  sortie?: unknown;
  erreur?: string;
  debut: Date;
  dureeMs: number;
}

/** Le document `orgs/{orgId}/fluxExecutions/{id}`. Ecrit par le serveur seul. */
export interface FluxExecution {
  id: string;
  orgId: string;
  fluxId: string;
  fluxNom: string;
  version: number;
  portee: FluxPortee;
  auteurUid: string;
  statut: FluxStatutExecution;
  test: boolean;
  declenchement: { type: FluxTypeDeclencheur | "test"; par?: string; at: Date };
  /** Declenchement « bouton » : la fiche du clic (niveau fiche), pour le suivi. */
  fiche?: { famille: string; id: string };
  /** Declenchement « bouton » : l'uid de qui a clique. Les actions Taki de l'execution tournent avec SES droits (`auteurUid` garde la main sur les connexions et variables). */
  executantUid?: string;
  /** Declenchement « bouton » : la clef du bouton (`cleDeBouton`). */
  bouton?: string;
  debut: Date;
  fin: Date | null;
  dureeMs: number | null;
  etapes: FluxExecutionEtape[];
  erreur?: string;
  pause?: { etapeId: string; raison: "delai" | "approbation"; reprendreA?: Date | null; message?: string };
}

/** Une variable (`orgs/{orgId}/fluxVariables/{id}`) ; une valeur SECRETE reste serveur. */
export interface FluxVariable {
  id: string;
  orgId: string;
  cle: string;
  /** Vide si la variable est secrete. */
  valeur: string;
  secrete: boolean;
  apercu: string;
  portee: FluxPortee;
  auteurUid: string;
  createdAt: Date;
  updatedAt: Date;
}

export const FluxVariableSaisieSchema = z
  .object({
    cle: z.string().regex(NOM_VARIABLE, "Cle invalide (lettres, chiffres, _)."),
    valeur: z.string().max(8000),
    secrete: z.boolean().default(false),
    portee: z.enum(FLUX_PORTEES).default("perso"),
  })
  .strict();

// ---------------------------------------------------------------------------
// Verification structurelle (au-dela de Zod)
// ---------------------------------------------------------------------------

export interface FluxProbleme {
  chemin: string;
  message: string;
}

/** Parcourt toutes les etapes, en profondeur, avec leur chemin et leur profondeur. */
export function parcourirEtapes(etapes: readonly FluxEtape[], visite: (etape: FluxEtape, chemin: string, profondeur: number) => void, prefixe = "", profondeur = 0): void {
  etapes.forEach((etape) => {
    const chemin = `${prefixe}${prefixe ? "." : ""}${etape.id}`;
    visite(etape, chemin, profondeur);
    if (etape.type === "branches") {
      etape.branches.forEach((branche) => parcourirEtapes(branche.etapes, visite, `${chemin}.${branche.id}`, profondeur + 1));
      parcourirEtapes(etape.sinon, visite, `${chemin}.sinon`, profondeur + 1);
    }
    if (etape.type === "boucle") parcourirEtapes(etape.etapes, visite, chemin, profondeur + 1);
  });
}

/** Toutes les etapes d'une definition, a plat. */
export function toutesLesEtapes(etapes: readonly FluxEtape[]): FluxEtape[] {
  const liste: FluxEtape[] = [];
  parcourirEtapes(etapes, (etape) => liste.push(etape));
  return liste;
}

/** Le prochain identifiant libre `step_N`. */
export function prochainIdEtape(definition: Pick<FluxDefinition, "etapes">): string {
  let max = 0;
  for (const etape of toutesLesEtapes(definition.etapes)) {
    const m = /^step_(\d+)$/.exec(etape.id);
    if (m) max = Math.max(max, Number(m[1]));
  }
  return `step_${max + 1}`;
}

/**
 * Ce que Zod ne voit pas : identifiants uniques, profondeur, nombre d'etapes,
 * references vers des etapes qui existent et qui PRECEDENT (une reference en
 * avant serait toujours vide a l'execution).
 */
export function verifierFlux(definition: FluxDefinition): FluxProbleme[] {
  const problemes: FluxProbleme[] = [];
  const vus = new Set<string>();
  let total = 0;
  const visibles = new Set<string>(["trigger", "vars", "date", "flux", "connexions"]);
  const verifierRefs = (valeur: unknown, chemin: string, connues: Set<string>): void => {
    for (const ref of referencesDe(valeur)) {
      const racine = ref.split(/[.[]/)[0] ?? "";
      if (!connues.has(racine)) problemes.push({ chemin, message: `Reference inconnue ou posterieure : {{${ref}}}.` });
    }
  };
  const marcher = (etapes: readonly FluxEtape[], prefixe: string, profondeur: number, connues: Set<string>): void => {
    if (profondeur > FLUX_PROFONDEUR_MAX) problemes.push({ chemin: prefixe || "etapes", message: `Trop d'imbrication (${FLUX_PROFONDEUR_MAX} niveaux au plus).` });
    for (const etape of etapes) {
      total += 1;
      const chemin = `${prefixe}${prefixe ? "." : ""}${etape.id}`;
      if (vus.has(etape.id)) problemes.push({ chemin, message: `Identifiant d'etape en double : ${etape.id}.` });
      vus.add(etape.id);
      if (etape.type === "taki" || etape.type === "connecteur") verifierRefs(etape.entrees, chemin, connues);
      if (etape.type === "http") verifierRefs([etape.url, etape.entetes, etape.parametres, etape.corps], chemin, connues);
      if (etape.type === "donnees") verifierRefs(etape.valeurs, chemin, connues);
      if (etape.type === "approbation") verifierRefs(etape.message, chemin, connues);
      if (etape.type === "agent") {
        verifierRefs(etape.consigne, chemin, connues);
        const cles = new Set<string>();
        for (const champ of etape.champsSortie ?? []) {
          if (cles.has(champ.cle)) problemes.push({ chemin, message: `Champ de sortie en double : ${champ.cle}.` });
          cles.add(champ.cle);
        }
        if (new Set(etape.outils).size !== etape.outils.length) problemes.push({ chemin, message: "Outil nommé deux fois." });
      }
      if (etape.type === "delai" && etape.jusqua) verifierRefs(etape.jusqua, chemin, connues);
      if (etape.type === "delai" && (etape.mode === "pour" ? etape.duree === undefined : etape.jusqua === undefined)) problemes.push({ chemin, message: "Un delai demande une duree, ou un instant pour « jusqu'a »." });
      if (etape.type === "branches") {
        const ids = new Set<string>();
        for (const branche of etape.branches) {
          if (ids.has(branche.id)) problemes.push({ chemin: `${chemin}.${branche.id}`, message: `Branche en double : ${branche.id}.` });
          ids.add(branche.id);
          verifierRefs(branche.conditions, `${chemin}.${branche.id}`, connues);
          marcher(branche.etapes, `${chemin}.${branche.id}`, profondeur + 1, new Set(connues));
        }
        marcher(etape.sinon, `${chemin}.sinon`, profondeur + 1, new Set(connues));
      }
      if (etape.type === "boucle") {
        verifierRefs(etape.elements, chemin, connues);
        const dedans = new Set(connues);
        dedans.add(etape.id);
        marcher(etape.etapes, chemin, profondeur + 1, dedans);
      }
      connues.add(etape.id);
    }
  };
  const declencheur = definition.declencheur;
  if (declencheur.type === "fiche") problemes.push(...verifierDeclencheurFiche(declencheur));
  if (declencheur.type === "connecteur") {
    if (strategieDeclencheur(declencheur.connecteur, declencheur.declencheur) === null) {
      problemes.push({ chemin: "declencheur", message: `Declencheur ${declencheur.declencheur} indisponible pour ${connecteurActif(declencheur.connecteur)?.libelle ?? declencheur.connecteur} (seuls le sondage et le webhook simple sont tenus).` });
    }
    // Un declencheur part avant toute etape : il ne cite rien.
    if (referencesDe(declencheur.entrees).length) problemes.push({ chemin: "declencheur", message: "Un declencheur ne cite pas de reference {{...}}." });
  }
  marcher(definition.etapes, "", 0, visibles);
  if (total > FLUX_ETAPES_MAX) problemes.push({ chemin: "etapes", message: `Trop d'etapes (${FLUX_ETAPES_MAX} au plus).` });
  return problemes;
}

/** Ce que Zod ne croise pas dans un declencheur fiche : le champ requis, les options propres a un evenement. */
export function verifierDeclencheurFiche(d: FluxDeclencheurFiche): FluxProbleme[] {
  const problemes: FluxProbleme[] = [];
  const chemin = "declencheur";
  if (d.evenement !== "champ" && (d.de !== undefined || d.vers !== undefined)) problemes.push({ chemin, message: "« De » et « Vers » ne valent que pour « Un champ change »." });
  if (d.evenement !== "date" && (d.decalage !== undefined || d.heure !== undefined)) problemes.push({ chemin, message: "Le décalage et l'heure ne valent que pour « Date atteinte »." });
  for (const [i, c] of (d.conditions ?? []).entries()) {
    if (referencesDe(c.valeur).length) problemes.push({ chemin: `${chemin}.conditions.${i}`, message: "Une condition du déclencheur ne cite pas de référence {{...}}." });
  }
  return problemes;
}

/**
 * CE QUI MANQUE POUR PUBLIER, au-dela de la structure : un brouillon se range
 * a moitie rempli (le builder enregistre a chaque geste), une automatisation
 * publiee non. Le champ surveille d'un declencheur qui l'exige, la valeur d'une
 * condition, la donnee d'une condition du routeur : sans eux, l'automatisation
 * partirait a vide ou ne partirait jamais, sans rien dire (constat du 03/11).
 */
export function problemesDePublication(definition: FluxDefinition): FluxProbleme[] {
  const problemes: FluxProbleme[] = [];
  const d = definition.declencheur;
  if (d.type === "fiche") {
    if (FLUX_EVENEMENTS_A_CHAMP.includes(d.evenement) && !d.champ) problemes.push({ chemin: "declencheur", message: "Choisissez le champ surveillé." });
    for (const [i, c] of (d.conditions ?? []).entries()) {
      if (!FLUX_OPERATEURS_UNAIRES.includes(c.operateur) && (c.valeur === undefined || c.valeur === "")) problemes.push({ chemin: `declencheur.conditions.${i}`, message: "Valeur requise pour cette condition." });
    }
  }
  if (d.type === "bouton" && (d.app === "" || d.porte === "" || d.bouton === "")) problemes.push({ chemin: "declencheur", message: "Choisissez le bouton : l'app, la porte, puis le bouton." });
  parcourirEtapes(definition.etapes, (etape, chemin) => {
    if (etape.type !== "branches") return;
    for (const branche of etape.branches) {
      if (branche.conditions.some((groupe) => groupe.some((c) => c.gauche.trim() === ""))) problemes.push({ chemin: `${chemin}.${branche.id}`, message: `Branche « ${branche.nom} » : une condition n'a pas de donnée à comparer.` });
      // « Comparé à » vide sur un operateur qui compare : « contient » rien serait vrai pour tout texte (relecture d'Astra, 03/11).
      if (branche.conditions.some((groupe) => groupe.some((c) => !FLUX_OPERATEURS_UNAIRES.includes(c.operateur) && (c.droite ?? "").trim() === ""))) problemes.push({ chemin: `${chemin}.${branche.id}`, message: `Branche « ${branche.nom} » : une condition n'a pas de valeur à comparer (« est vide » si c'est voulu).` });
    }
  });
  return problemes;
}

/** Valide une definition A PUBLIER : `validerDefinitionFlux` puis `problemesDePublication`. */
export function validerPublicationFlux(valeur: unknown): FluxDefinition {
  const definition = validerDefinitionFlux(valeur);
  const problemes = problemesDePublication(definition);
  if (problemes.length) throw Object.assign(new Error(problemes.slice(0, 8).map((p) => `${p.chemin} : ${p.message}`).join(" ; ")), { problemes });
  return definition;
}

/**
 * UNE VALEUR STOCKEE correspond-elle a la valeur attendue (texte) ? Une liste
 * (choix multiple, liens) correspond si l'un de ses elements correspond ; un
 * nombre ou un booleen se compare a son texte. `attendue` absente : tout.
 */
export function valeurCorrespond(valeur: unknown, attendue: string | undefined): boolean {
  if (attendue === undefined) return true;
  if (Array.isArray(valeur)) return valeur.some((v) => valeurCorrespond(v, attendue));
  if (valeur === null || valeur === undefined) return false;
  if (typeof valeur === "object") return false;
  return String(valeur).trim() === attendue.trim();
}

/** Les conditions d'un declencheur fiche sont-elles vraies pour cette fiche ? Sans condition : oui. */
export function conditionsFicheVraies(d: Pick<FluxDeclencheurFiche, "conditions" | "conditionsMode">, fiche: Record<string, unknown>, evaluer: (c: FluxCondition, contexte: Record<string, unknown>) => boolean): boolean {
  const conditions = d.conditions ?? [];
  if (conditions.length === 0) return true;
  const verdicts = conditions.map((c) => evaluer({ gauche: `{{fiche.${c.champ}}}`, operateur: c.operateur, ...(c.valeur === undefined ? {} : { droite: c.valeur }) }, { fiche }));
  return d.conditionsMode === "une" ? verdicts.some(Boolean) : verdicts.every(Boolean);
}

/**
 * LES ETAPES QUI ENVOIENT HORS DE L'EQUIPE : ce qu'un clic de bouton ne lance
 * pas sans un oui explicite (`docs/socle/smart-boutons.md`, « Envoi hors de
 * l'equipe »). Une requete HTTP, une action de connecteur qui n'est pas une
 * lecture, l'envoi d'un e-mail (`mail_send`), une approbation par e-mail ou
 * WhatsApp, un agent qui peut appeler un de ces outils. PAS `send_notification` :
 * elle ne vise que des membres de l'organisation. Une etape desactivee ne tourne
 * pas : elle ne compte pas. Le libelle se lit tel quel dans la fenetre de
 * confirmation, et DIT CE QUI PART : « E-mail à luc@exemple.lu », « Appel web
 * vers api.exemple.org », sinon le nom de l'etape.
 */
export interface SortieExterne {
  etapeId: string;
  libelle: string;
}

/** Les actions Taki qui envoient hors de l'equipe (un agent qui les a comme outil aussi). */
const ACTIONS_TAKI_EXTERNES: Readonly<Record<string, string>> = { mail_send: "E-mail" };

const PENDANT_L_EXECUTION = "destinataire déterminé pendant l'exécution";

/**
 * L'e-mail d'un flux ne joint JAMAIS de fichier (`mail_send` n'a pas de
 * parametre de pieces jointes, le message part en texte seul) : la fenetre le
 * dit, plutot que de laisser croire que les fichiers de la fiche suivent.
 */
const SANS_PIECE_JOINTE = "(sans pièce jointe)";

/** Une reference `{{...}}` que le clic connait deja : la racine `trigger`, et rien d'autre. */
const RACINE_TRIGGER = /^trigger(?:[.[]|$)/;

/**
 * UNE VALEUR D'ENTREE, resolue avec le seul `{{trigger}}` du clic, ou `null`
 * quand elle depend d'autre chose (une etape d'avant, une variable : seule
 * l'execution le saura) ou que la reference reste vide.
 */
function valeurDuClic(brute: unknown, trigger: unknown): unknown {
  if (brute === undefined || brute === null || brute === "") return null;
  if (referencesDe(brute).some((reference) => !RACINE_TRIGGER.test(reference))) return null;
  const resolue = resoudre(brute, { trigger });
  return resolue === undefined || resolue === null || resolue === "" ? null : resolue;
}

/** Les destinataires d'une valeur (liste, ou texte separe par des virgules), ou `[]` si elle reste un modele. */
function destinatairesDe(brute: unknown, trigger: unknown): string[] {
  const valeur = valeurDuClic(brute, trigger);
  if (valeur === null) return [];
  const elements = Array.isArray(valeur) ? valeur : [valeur];
  const textes = elements.flatMap((element) => (typeof element === "string" ? element.split(/[,;]/) : []));
  const nets = textes.map((texte) => texte.trim()).filter((texte) => texte !== "");
  return nets.some((texte) => texte.includes("{{")) ? [] : nets;
}

/** « <Canal> à a, b » ou « <Canal> : destinataire determine pendant l'execution ». */
function vers(canal: string, destinataires: readonly string[]): string {
  return destinataires.length === 0 ? `${canal} : ${PENDANT_L_EXECUTION}` : `${canal} à ${destinataires.join(", ")}`;
}

/** L'hote d'une adresse web, ou `null` si l'adresse reste un modele ou n'en est pas une. */
function hoteDe(brute: unknown, trigger: unknown): string | null {
  const valeur = valeurDuClic(brute, trigger);
  if (typeof valeur !== "string") return null;
  try {
    return new URL(valeur).hostname || null;
  } catch {
    return null;
  }
}

/**
 * LES SORTIES D'UN FLUX, chacune avec un libelle qui dit ce qui part. `trigger`
 * est la sortie du declencheur du clic (la fiche et qui clique) : les
 * references `{{trigger...}}` des destinataires s'y resolvent (`resoudre`, de
 * `flux-expressions.ts`). Une reference a une etape d'avant ne le peut pas : le
 * libelle dit alors que le destinataire sera determine pendant l'execution.
 */
export function sortiesExternes(definition: Pick<FluxDefinition, "etapes">, trigger?: unknown): SortieExterne[] {
  const sorties: SortieExterne[] = [];
  parcourirEtapes(definition.etapes, (etape) => {
    if (etape.desactivee === true) return;
    const de = (libelle: string): void => {
      sorties.push({ etapeId: etape.id, libelle });
    };
    switch (etape.type) {
      case "http": {
        const hote = hoteDe(etape.url, trigger);
        de(hote === null ? etape.nom : `Appel web vers ${hote}`);
        break;
      }
      case "connecteur":
        if (classementAction(etape.connecteur, etape.action) !== "lecture") de(etape.nom);
        break;
      case "taki": {
        const canal = ACTIONS_TAKI_EXTERNES[etape.action];
        if (canal !== undefined) de(`${vers(canal, destinatairesDe(etape.entrees["to"], trigger))} ${SANS_PIECE_JOINTE}`);
        break;
      }
      case "approbation": {
        const mail = etape.canaux.includes("email");
        const whatsapp = etape.canaux.includes("whatsapp");
        if (mail || whatsapp) {
          const canal = mail && whatsapp ? "E-mail et WhatsApp" : mail ? "E-mail" : "WhatsApp";
          de(vers(canal, destinatairesDe(etape.destinataires, trigger)));
        }
        break;
      }
      case "agent":
        if (etape.outilsMcp === true || etape.outils.some((outil) => ACTIONS_TAKI_EXTERNES[outil] !== undefined)) de(etape.nom);
        break;
      default:
        break;
    }
  });
  return sorties;
}

/** Les references `{{...}}` d'une valeur quelconque (texte, liste, objet). */
export function referencesDe(valeur: unknown): string[] {
  const refs: string[] = [];
  const visiter = (v: unknown): void => {
    if (typeof v === "string") {
      for (const m of v.matchAll(/\{\{\s*([^{}]+?)\s*\}\}/g)) if (m[1]) refs.push(m[1]);
    } else if (Array.isArray(v)) v.forEach(visiter);
    else if (v !== null && typeof v === "object") Object.values(v).forEach(visiter);
  };
  visiter(valeur);
  return refs;
}

/** Valide une saisie complete : Zod puis `verifierFlux`. Leve une erreur lisible. */
export function validerSaisieFlux(valeur: unknown): FluxSaisie {
  const verdict = FluxSaisieSchema.safeParse(valeur);
  if (!verdict.success) {
    throw Object.assign(new Error(verdict.error.issues.slice(0, 8).map((i) => `${i.path.join(".") || "flux"} : ${i.message}`).join(" ; ")), { issues: verdict.error.issues });
  }
  const refus = refusDeBouton(verdict.data.portee, verdict.data.definition.declencheur);
  if (refus !== null) throw Object.assign(new Error(`declencheur : ${refus}`), { problemes: [{ chemin: "declencheur", message: refus }] });
  const problemes = verifierFlux(verdict.data.definition);
  if (problemes.length) throw Object.assign(new Error(problemes.slice(0, 8).map((p) => `${p.chemin} : ${p.message}`).join(" ; ")), { problemes });
  return verdict.data;
}

/** Valide une definition seule (le brouillon du builder). */
export function validerDefinitionFlux(valeur: unknown): FluxDefinition {
  const verdict = FluxDefinitionSchema.safeParse(valeur);
  if (!verdict.success) {
    throw Object.assign(new Error(verdict.error.issues.slice(0, 8).map((i) => `${i.path.join(".") || "definition"} : ${i.message}`).join(" ; ")), { issues: verdict.error.issues });
  }
  const problemes = verifierFlux(verdict.data);
  if (problemes.length) throw Object.assign(new Error(problemes.slice(0, 8).map((p) => `${p.chemin} : ${p.message}`).join(" ; ")), { problemes });
  return verdict.data;
}

/** Une definition vide : declencheur manuel, aucune etape (l'ecran « + Ajouter »). */
export function definitionVide(): FluxDefinition {
  return { declencheur: { type: "manuel" }, etapes: [] };
}

// ---------------------------------------------------------------------------
// Forme de stockage (Firestore)
// ---------------------------------------------------------------------------

/**
 * FIRESTORE REFUSE LES LISTES DANS LES LISTES. Les conditions d'un routeur
 * sont des groupes (OU) de conditions (ET) : `FluxCondition[][]`. Dans la
 * base, chaque groupe devient `{ et: [...] }`. Le serveur ecrit par
 * `definitionVersStockage`, et tout lecteur (moteur, SDK, MCP) relit par
 * `definitionDepuisStockage`, qui accepte aussi la forme brute.
 */
export function definitionVersStockage(definition: FluxDefinition): Record<string, unknown> {
  const etapes = (liste: readonly FluxEtape[]): unknown[] =>
    liste.map((etape) => {
      if (etape.type === "connecteur") return { ...etape, entrees: entreesVersStockage(etape.entrees) };
      if (etape.type === "branches") {
        return {
          ...etape,
          branches: etape.branches.map((b) => ({ ...b, conditions: b.conditions.map((groupe) => ({ et: groupe })), etapes: etapes(b.etapes) })),
          sinon: etapes(etape.sinon),
        };
      }
      if (etape.type === "boucle") return { ...etape, etapes: etapes(etape.etapes) };
      return etape;
    });
  const declencheur = definition.declencheur.type === "connecteur" ? { ...definition.declencheur, entrees: entreesVersStockage(definition.declencheur.entrees) } : definition.declencheur;
  return JSON.parse(JSON.stringify({ declencheur, etapes: etapes(definition.etapes) })) as Record<string, unknown>;
}

/**
 * LES ENTREES D'UN CONNECTEUR peuvent porter une liste de listes (les lignes
 * d'un tableur, par exemple) : Firestore la refuse. Elles se rangent alors en
 * TEXTE JSON, `{ "$json": "..." }`, et se relisent par `entreesDepuisStockage`.
 * Des entrees sans liste de listes restent telles quelles, lisibles en base.
 */
const CLE_JSON = "$json";
function contientListeDeListes(valeur: unknown, dansListe = false): boolean {
  if (Array.isArray(valeur)) return dansListe || valeur.some((v) => contientListeDeListes(v, true));
  if (valeur !== null && typeof valeur === "object") return Object.values(valeur).some((v) => contientListeDeListes(v, false));
  return false;
}
export function entreesVersStockage(entrees: Record<string, unknown>): Record<string, unknown> {
  return contientListeDeListes(entrees) ? { [CLE_JSON]: JSON.stringify(entrees) } : entrees;
}
export function entreesDepuisStockage(brut: unknown): unknown {
  if (brut === null || typeof brut !== "object" || Array.isArray(brut)) return brut;
  const o = brut as Record<string, unknown>;
  const cles = Object.keys(o);
  if (cles.length !== 1 || cles[0] !== CLE_JSON || typeof o[CLE_JSON] !== "string") return brut;
  try {
    return JSON.parse(o[CLE_JSON]) as unknown;
  } catch {
    return brut;
  }
}

/** L'inverse de `definitionVersStockage`, tolerant : une definition deja brute passe telle quelle. */
export function definitionDepuisStockage(brute: unknown): unknown {
  if (typeof brute === "string") {
    try {
      return definitionDepuisStockage(JSON.parse(brute));
    } catch {
      return brute;
    }
  }
  if (brute === null || typeof brute !== "object") return brute;
  const groupe = (g: unknown): unknown => (g !== null && typeof g === "object" && !Array.isArray(g) && Array.isArray((g as { et?: unknown }).et) ? (g as { et: unknown[] }).et : g);
  const etapes = (liste: unknown): unknown =>
    !Array.isArray(liste)
      ? liste
      : liste.map((e) => {
          if (e === null || typeof e !== "object") return e;
          const etape = { ...(e as Record<string, unknown>) };
          if (etape["type"] === "connecteur") etape["entrees"] = entreesDepuisStockage(etape["entrees"]);
          if (etape["type"] === "branches") {
            if (Array.isArray(etape["branches"])) {
              etape["branches"] = (etape["branches"] as unknown[]).map((b) =>
                b === null || typeof b !== "object"
                  ? b
                  : { ...(b as Record<string, unknown>), conditions: Array.isArray((b as Record<string, unknown>)["conditions"]) ? ((b as Record<string, unknown>)["conditions"] as unknown[]).map(groupe) : (b as Record<string, unknown>)["conditions"], etapes: etapes((b as Record<string, unknown>)["etapes"]) },
              );
            }
            etape["sinon"] = etapes(etape["sinon"]);
          }
          if (etape["type"] === "boucle") etape["etapes"] = etapes(etape["etapes"]);
          return etape;
        });
  const d = brute as Record<string, unknown>;
  const declencheur = d["declencheur"];
  const declencheurLu =
    declencheur !== null && typeof declencheur === "object" && (declencheur as Record<string, unknown>)["type"] === "connecteur"
      ? { ...(declencheur as Record<string, unknown>), entrees: entreesDepuisStockage((declencheur as Record<string, unknown>)["entrees"]) }
      : declencheur;
  return { ...d, ...(declencheur === undefined ? {} : { declencheur: declencheurLu }), etapes: etapes(d["etapes"]) };
}
